/**
 * ── WHERE A BLOCK IS ALLOWED TO SIT (2026-08-27) ───────────────────────────────
 *
 * Layne, `GUARDRAIL-KEYS-TO-READ-20260827.md`: two of the five keys she declared and the
 * Hub ignored are placement rules — `lockedPosition` and `mustImmediatelyFollow` — plus
 * `mustImmediatelyPrecede`, which is live on two blocks and did not make her list.
 * Observed in the tool: "Logo, Contact Us, Social and Disclaimer can be moved" and "CTA
 * row can be moved off the copy block."
 *
 * Everything here is read off the LIVE headers (`/api/sfmc-blocks?fresh=1`, 42 blocks,
 * pulled 2026-08-27), not off the examples in her note — the amenity `rowSplit` build
 * taught that lesson: the keys were nested one level deeper than the report showed, and
 * building from the report would have shipped a no-op. Verbatim from that pull:
 *
 *   ECH_Logo             placement.position "first"
 *   ECH_ContactUsDefault placement.position -3
 *   ECH_SocialIcons      placement.position -2  + mustImmediatelyPrecede ECH_Disclaimer
 *   ECH_Disclaimer       placement.position "last"
 *   ECH_1CTA / ECH_2CTA  mustImmediatelyFollow + movesWith ECH_CopyBlock
 *   ECH_TableHeader      mustImmediatelyPrecede ECH_ListingTable |
 *                        "firstOf:ECH_Featured1CTA|ECH_Featured2CTA"
 *
 * TWO DECISIONS WORTH KEEPING.
 *
 * 1 · NEGATIVE POSITIONS COUNT FROM THE END, and that is the whole point — Layne:
 *     "templates range from 7 to 12 blocks and an author can add up to 5 featured cards,
 *     so an absolute footer position would break the moment they did." `-2` is
 *     second-from-last at any length.
 *
 * 2 · `mustImmediatelyFollow` and `movesWith` ARE SEPARATE, and she said so explicitly:
 *     the first is a validity constraint (the CTA row may never sit above the copy block,
 *     and nothing may come between them), the second is a drag behaviour (moving the copy
 *     block carries the CTA row with it). A tool could implement either alone, so neither
 *     is inferred from the other here.
 *
 * PURE MODULE, NO REACT. The Studio wires `lockedIds` to the drag handle, `reorder` to
 * both the drag drop and the ↑↓ buttons, and `insertBlocked` to the palette insert — one
 * source of truth, so the canvas cannot disagree with the gate about a legal move.
 */
import { echMeta, echBlocksMeta } from "./echRegistry";
import { echTplRules } from "./echTemplates";

/** what a block's own `placement` object declares (live shape, 2026-08-27) */
export interface BlockPlacement {
  locked?: boolean;
  /** "first" | "last" | an integer; NEGATIVE counts from the end */
  position?: "first" | "last" | number;
  mustImmediatelyFollow?: string;
  mustImmediatelyPrecede?: string;
  movesWith?: string;
  /** the generic block that wraps THIS block on either side and travels with it (a 25px spacer around the subhead) */
  bracketedBy?: string;
  /** Layne's own sentence — shown verbatim when a move is refused */
  message?: string;
}

/** the minimum a placement check needs off a document */
export interface PlaceBlock { id: string; type: string }

const S = (v: unknown): string => (v == null ? "" : String(v));

/** the SFMC block name for a doc entry ("" when it is not an ECH block) */
export function nameOf(b: PlaceBlock | null | undefined): string {
  if (!b) return "";
  const m = echMeta(S(b.type));
  return S(m && m.echName);
}

/** the block's declared placement, or null. Reads the FULL rule set — `gr` is
 *  cap-filtered and drops everything that is not a field. */
export function placementOf(type: string): BlockPlacement | null {
  const m = echMeta(S(type));
  const full = (m && (m.full as unknown as { placement?: BlockPlacement })) || null;
  const p = full && full.placement;
  return p && typeof p === "object" ? p : null;
}

/**
 * Resolve a declared position against a document length.
 * "first" → 0, "last" → len-1, -2 → len-2, 3 → 3. NaN for anything unreadable.
 */
export function pinIndex(position: unknown, len: number): number {
  if (position === "first") return 0;
  if (position === "last") return Math.max(0, len - 1);
  const n = typeof position === "number" ? position : parseInt(S(position), 10);
  if (!isFinite(n)) return NaN;
  return n < 0 ? len + n : n;
}

/** every pinned (block name → declared position) pair for a template, template rule
 *  first, then any block-level `placement.position` the rule did not already name. */
export function pinnedRules(tplName?: string): { block: string; position: "first" | "last" | number }[] {
  const out: { block: string; position: "first" | "last" | number }[] = [];
  const seen: Record<string, 1> = {};
  const add = (block: string, position: unknown) => {
    const b = S(block);
    if (!b || seen[b] || position == null) return;
    seen[b] = 1;
    out.push({ block: b, position: position as "first" | "last" | number });
  };
  for (const r of echTplRules(S(tplName))) {
    if (S(r.type) !== "lockedPosition") continue;
    for (const a of (r as unknown as { appliesAt?: { block?: string; position?: unknown }[] }).appliesAt || []) {
      add(S(a && a.block), a && a.position);
    }
  }
  return out;
}

/** true when this block may not be dragged or reordered at all */
export function isPinned(b: PlaceBlock, tplName?: string): boolean {
  const name = nameOf(b);
  if (!name) return false;
  if (pinnedRules(tplName).some((p) => p.block === name)) return true;
  const p = placementOf(b.type);
  /* a block-level pin counts even with no template rule — a bespoke doc still has
     a footer, and `locked: true` with no `position` is a follow/precede rule rather
     than a pin, so the position is what makes it one */
  return !!(p && p.locked && p.position != null);
}

/** the ids the Studio must not offer a drag handle for */
export function lockedIds(doc: PlaceBlock[] | null | undefined, tplName?: string): Set<string> {
  const out = new Set<string>();
  for (const b of doc || []) if (isPinned(b, tplName)) out.add(b.id);
  return out;
}

/** the follow rules in force: this block must sit DIRECTLY AFTER one of `targets` */
export function followOf(b: PlaceBlock, tplName?: string): { targets: string[]; movesWith: string; message: string } | null {
  const name = nameOf(b);
  if (!name) return null;
  for (const r of echTplRules(S(tplName))) {
    if (S(r.type) !== "mustImmediatelyFollow") continue;
    if (!(r.applies || []).some((a) => S(a) === name)) continue;
    const rr = r as unknown as { targets?: string[]; movesWith?: string };
    return {
      targets: (rr.targets || []).map(S).filter(Boolean),
      movesWith: S(rr.movesWith),
      message: S(r.message),
    };
  }
  const p = placementOf(b.type);
  if (p && p.mustImmediatelyFollow) {
    return { targets: [S(p.mustImmediatelyFollow)], movesWith: S(p.movesWith), message: S(p.message) };
  }
  return null;
}

/**
 * ── `movesWith` ON A "MUST PRECEDE" RULE (2026-10-27, Layne, Teams) ──────────────────────
 * "After adding a Listing Table (its header comes in above it), I can move Visit Us or
 * Amenities below the Listing Table … but I can't move the Listing Table above them. The
 * header is pinned directly above it by tableheader-placement, and nothing makes the header
 * move with it, so the move is refused." Her fix, same as the CTA row under the copy block:
 * `movesWith` on the tableheader-placement rule, with the same target list, including
 * `firstOf:ECH_Featured1CTA|ECH_Featured2CTA`.
 *
 * What the value may be: the same array as `targets`, a single spec (a block name or a
 * `firstOf:A|B`), or `true` / `"targets"` meaning "whichever of my targets I sit on". With
 * no `movesWith` the rule behaves exactly as before.
 */
function moveSpecs(v: unknown, targets: string[]): string[] {
  if (v === true || v === "true" || v === "targets" || v === "*") return targets.slice();
  if (Array.isArray(v)) return v.map(S).map((x) => x.trim()).filter(Boolean);
  /* echTemplates normalises a rule's `movesWith` to a string, so an array declared in the
     header arrives comma-joined. Block names never hold a comma (`firstOf:` joins with |). */
  return S(v).split(",").map((x) => x.trim()).filter(Boolean);
}

/** …and the mirror: this block must sit DIRECTLY BEFORE one of `targets` */
export function precedeOf(b: PlaceBlock, tplName?: string): { targets: string[]; message: string; movesWith: string[] } | null {
  const name = nameOf(b);
  if (!name) return null;
  for (const r of echTplRules(S(tplName))) {
    if (S(r.type) !== "mustImmediatelyPrecede") continue;
    if (!(r.applies || []).some((a) => S(a) === name)) continue;
    const rr = r as unknown as { targets?: string[]; movesWith?: unknown };
    const targets = (rr.targets || []).map(S).filter(Boolean);
    return { targets, message: S(r.message), movesWith: moveSpecs(rr.movesWith, targets) };
  }
  const p = placementOf(b.type);
  if (p && p.mustImmediatelyPrecede) {
    const targets = [S(p.mustImmediatelyPrecede)];
    return { targets, message: S(p.message), movesWith: moveSpecs(p.movesWith, targets) };
  }
  return null;
}

/**
 * The block that rides a `movesWith` precede rule, and what it carries: the header at
 * `header`, its target right below it, and — when that target is a `firstOf:A|B` run of
 * Featured cards — every card of the run that follows (Jack's answer to Layne's second
 * question: the header belongs to all the cards under it, so the run moves as one).
 * `i` may be the header or any block of its run. Null when no such rule is in force here.
 */
export function headerRun(doc: PlaceBlock[] | null | undefined, i: number, tplName?: string): { header: number; end: number } | null {
  const d = doc || [];
  const at = (h: number): { header: number; end: number } | null => {
    if (h < 0 || h + 1 >= d.length) return null;
    const p = precedeOf(d[h], tplName);
    if (!p || !p.movesWith.length) return null;
    const spec = p.movesWith.find((s) => targetMatch(s, nameOf(d[h + 1]), d, h + 1));
    if (!spec) return null;
    let end = h + 1;
    if (spec.indexOf("firstOf:") === 0) {
      const opts = spec.slice(8).split("|").map(S).filter(Boolean);
      while (end + 1 < d.length && opts.indexOf(nameOf(d[end + 1])) > -1) end++;
    }
    return { header: h, end };
  };
  /* the header itself, or a header at most a run's length above (a run is ≤ 5 cards) */
  for (let h = i; h >= Math.max(0, i - 6); h--) {
    const r = at(h);
    if (r && i <= r.end) return r;
    if (h < i && r) return null;
  }
  return null;
}

/**
 * ── A SECTION HEADER LEAVES WITH ITS SECTION (2026-10-30, Justin Bradford, SLC email
 * 1041310: "they did not want the featured homes module, but the module stayed in there") ──
 * The requester removed every Featured card and the blue "Featured Homes / View All" bar
 * stayed above nothing. The Milestone 2 tidy (`m2DropOrphanHeads`) already drops such a
 * header, but only in the block-palette test mode, so prod never did.
 *
 * Given the document before and after a delete: every block with a "must immediately
 * precede" rule (ECH_TableHeader → the Listing Table or the first Featured card) that had
 * its target under it BEFORE and has none AFTER. Pinned blocks are never returned (Social
 * icons precede the Disclaimer, and both are pinned), and a header that already stood
 * alone is left as it was — only the delete that took its section takes it.
 */
export function orphanedHeaders(before: PlaceBlock[] | null | undefined, after: PlaceBlock[] | null | undefined, tplName?: string): string[] {
  const b = before || [], a = after || [];
  const satisfied = (doc: PlaceBlock[], i: number): boolean => {
    const p = precedeOf(doc[i], tplName);
    if (!p || !p.targets.length) return true;
    const next = doc[i + 1];
    return !!next && p.targets.some((t) => targetMatch(t, nameOf(next), doc, i + 1));
  };
  const out: string[] = [];
  a.forEach((blk, i) => {
    if (!precedeOf(blk, tplName) || isPinned(blk, tplName)) return;
    const was = b.findIndex((x) => x.id === blk.id);
    if (was < 0 || !satisfied(b, was)) return;
    if (!satisfied(a, i)) out.push(blk.id);
  });
  return out;
}

/**
 * Does `name` satisfy a target spec? Plain name, or the live `firstOf:A|B` form —
 * ECH_TableHeader may precede the FIRST featured module of either variant, and only the
 * first ("modules 2 through 5 follow it with no header of their own").
 */
function targetMatch(spec: string, name: string, doc: PlaceBlock[], idx: number): boolean {
  const s = S(spec);
  if (!s) return false;
  if (s.indexOf("firstOf:") !== 0) return s === name;
  const opts = s.slice(8).split("|").map(S).filter(Boolean);
  if (opts.indexOf(name) < 0) return false;
  /* the first of those variants in the whole document, so a later card cannot claim it */
  const first = doc.findIndex((x) => opts.indexOf(nameOf(x)) > -1);
  return first === idx;
}

/**
 * The block ids that move TOGETHER with `id` — a copy block carries its CTA row.
 * Returns the ids in document order, `[id]` when nothing is coupled to it.
 */
export function unitOf(doc: PlaceBlock[] | null | undefined, id: string, tplName?: string): string[] {
  const d = doc || [];
  const i = d.findIndex((b) => b.id === id);
  if (i < 0) return [];
  const br = bracketUnit(d, i);
  if (br.length > 1) return br;
  /* a header with `movesWith` travels with its target: clicking the header, or the
     target (the Listing Table, the FIRST Featured card), moves the header and the run.
     A later card of the run keeps its own one-block move. */
  const hr = headerRun(d, i, tplName);
  if (hr && i <= hr.header + 1) return d.slice(hr.header, hr.end + 1).map((b) => b.id);
  const name = nameOf(d[i]);
  const ids = [d[i].id];
  /* the follower sits immediately after and names this block in `movesWith` */
  const nx = d[i + 1];
  if (nx) {
    const f = followOf(nx, tplName);
    if (f && f.movesWith && f.movesWith === name) ids.push(nx.id);
  }
  /* dragging the FOLLOWER moves the pair too — it cannot be separated from its target */
  const f0 = followOf(d[i], tplName);
  if (f0 && f0.movesWith) {
    const pv = d[i - 1];
    if (pv && nameOf(pv) === f0.movesWith) return [pv.id, d[i].id];
  }
  return ids;
}

/* ── THE SPACERS TRAVEL WITH THE SUBHEAD (2026-09-17) ───────────────────────────────────
   Justin Bradford, Teams 09-17, on asset 1034130: "there are two 25px spacers below Farmington
   instead of one above and one below." Every template assembles Spacer → Community subhead →
   Spacer (verified on QA the same day), so the pair was split in the Studio — the subhead has
   its own arrows and nothing tied the spacers to it. This is a Studio rule, not a block or
   template change (Layne's blocks and the TPL definitions are untouched): a 25px spacer that
   sits directly above or below the subhead is PART of the subhead's unit. Its own arrows are
   dead with this sentence, the subhead's arrows move all three, and any other block steps over
   the trio as one item — the same treatment `movesWith` gives the CTA row under Body Copy.
   The spacer is a generic block, so the declaration hangs off the block it wraps — see
   `brackets()` below for where the rule is read from. */
export interface Bracket { around: string; by: string; message: string }
/* ── THE RULE IS DECLARED IN THE BLOCK HEADER, LIKE EVERY OTHER PLACEMENT RULE (Layne, Teams
   2026-09-17: "I have guardrails on blocks that can't be separated/should move together, so I'd
   rather stay consistent and keep those rules all in the blocks"). The centre block's header
   carries `placement.bracketedBy: "<spacer block>"` (+ optional `message`); the Studio reads it
   off the live pull. The spacer itself is generic, so the key lives on the block it wraps.
   `BRACKETS_DEFAULT` is the fallback for a library whose subhead header does not carry the key
   yet — it is superseded, not merged, the moment any block declares one. */
export const BRACKETS_DEFAULT: Bracket[] = [
  { around: "ECH_CommunitySubHead", by: "ECH_SectionSpacer25", message: "The 25px spaces move with the Community subhead — use its arrows." },
];
/** the brackets in force: every block whose header declares `placement.bracketedBy`, else the default */
export function brackets(): Bracket[] {
  const out: Bracket[] = [];
  for (const m of echBlocksMeta()) {
    const p = (m.full as unknown as { placement?: BlockPlacement } | null)?.placement;
    const by = p && S(p.bracketedBy);
    if (!by || !m.echName) continue;
    out.push({ around: m.echName, by, message: S(p!.message) || ("These blocks move with " + m.echName.replace(/^ECH_/, "") + " — use its arrows.") });
  }
  return out.length ? out : BRACKETS_DEFAULT;
}
/** @deprecated read `brackets()` — kept so existing imports compile */
export const BRACKETS = BRACKETS_DEFAULT;
/** the ids of the bracket unit block `i` belongs to (in document order), or [] when it is in none */
export function bracketUnit(doc: PlaceBlock[] | null | undefined, i: number): string[] {
  const d = doc || [];
  const b = d[i];
  if (!b) return [];
  const name = nameOf(b);
  for (const r of brackets()) {
    /* the centre: take the spacer on either side */
    if (name === r.around) {
      const ids = [b.id];
      if (i > 0 && nameOf(d[i - 1]) === r.by) ids.unshift(d[i - 1].id);
      if (i + 1 < d.length && nameOf(d[i + 1]) === r.by) ids.push(d[i + 1].id);
      return ids.length > 1 ? ids : [];
    }
    /* a spacer: part of the unit only when it touches the centre */
    if (name === r.by) {
      if (i > 0 && nameOf(d[i - 1]) === r.around) return bracketUnit(d, i - 1);
      if (i + 1 < d.length && nameOf(d[i + 1]) === r.around) return bracketUnit(d, i + 1);
    }
  }
  return [];
}
/** the bracket's sentence when insertion index `at` would land inside a bracket unit of `doc`, else "" */
export function splitsBracket(doc: PlaceBlock[] | null | undefined, at: number): string {
  const d = doc || [];
  if (at <= 0 || at >= d.length) return "";
  const u = bracketUnit(d, at - 1);
  if (u.length > 1 && u.indexOf(d[at].id) > -1) {
    const r = brackets().find((x) => nameOf(d[at]) === x.around || nameOf(d[at - 1]) === x.around);
    return r ? r.message : "These blocks move together.";
  }
  return "";
}
/** the sentence for a block whose own arrows are dead because it rides in a bracket unit it does not lead */
export function bracketRider(doc: PlaceBlock[] | null | undefined, i: number): string {
  const d = doc || [];
  const b = d[i];
  if (!b) return "";
  const name = nameOf(b);
  for (const r of brackets()) {
    if (name !== r.by) continue;
    if ((i > 0 && nameOf(d[i - 1]) === r.around) || (i + 1 < d.length && nameOf(d[i + 1]) === r.around)) return r.message;
  }
  return "";
}

export interface PlaceVerdict { ok: boolean; message?: string }

/** Layne's sentence when she wrote one, else a plain one-liner. */
const say = (message: string, fallback: string): string => message || fallback;

/**
 * Would putting `ids` (a unit, in order) at insertion index `to` break a rule?
 * `to` is an INSERTION index into the document with the unit removed — the same index
 * the Studio's drop line and `insertAt` already use.
 */
export function canPlace(
  doc: PlaceBlock[] | null | undefined,
  ids: string[],
  to: number,
  tplName?: string,
): PlaceVerdict {
  const d = doc || [];
  const moving = ids.filter(Boolean);
  if (!moving.length) return { ok: true };
  const rest = d.filter((b) => moving.indexOf(b.id) < 0);
  const unit = moving.map((id) => d.find((b) => b.id === id)!).filter(Boolean);
  const at = Math.max(0, Math.min(rest.length, to));
  /* nothing lands inside a bracket (spacer · subhead · spacer) — `reorder` then steps over it */
  const split = splitsBracket(rest, at);
  if (split) return { ok: false, message: split };
  const next: PlaceBlock[] = [...rest.slice(0, at), ...unit, ...rest.slice(at)];

  /* the unit itself must not be pinned */
  for (const b of unit) {
    if (isPinned(b, tplName)) {
      const p = placementOf(b.type);
      return { ok: false, message: say(S(p && p.message), nameOf(b) + " is pinned and cannot be moved.") };
    }
  }
  /* …and nothing may land where a pinned block belongs, or between the pinned ones.
     ── ONLY A NEW PROBLEM REFUSES A MOVE (2026-09-10, Jack: "not able to shift the first
     few up or down, but if there's a blank view it will allow that") ────────────────────
     This judged the WHOLE resulting document, so a document that already broke a rule
     anywhere (a duplicate, an older draft, a footer out of place) froze every ↑↓ on every
     block, with a message about a block nobody was moving. `insertBlocked` below already
     counts only what the change introduces; a move is held to the same standard — a
     problem the document had before the move is not this move's fault. */
  const before: Record<string, 1> = {};
  placementProblems(d, tplName).forEach((p) => { before[(p.id || p.block) + "\u0000" + p.message] = 1; });
  const fresh = placementProblems(next, tplName).filter((p) => !before[(p.id || p.block) + "\u0000" + p.message]);
  if (fresh.length) return { ok: false, message: fresh[0].message };
  return { ok: true };
}

export interface PlaceProblem { id?: string; block: string; message: string }

/**
 * Every placement rule this document breaks, in reading order. Used by `canPlace` to
 * judge a proposed move, and available to the Done gate for a document that arrived
 * wrong some other way (a duplicate, an older draft).
 */
export function placementProblems(doc: PlaceBlock[] | null | undefined, tplName?: string): PlaceProblem[] {
  const d = doc || [];
  const out: PlaceProblem[] = [];
  const len = d.length;
  if (!len) return out;

  /* 1 · pinned positions */
  for (const pin of pinnedRules(tplName)) {
    const idx = d.findIndex((b) => nameOf(b) === pin.block);
    if (idx < 0) continue;                       /* not in this template — inert, not broken */
    const want = pinIndex(pin.position, len);
    if (!isFinite(want) || idx === want) continue;
    const b = d[idx];
    const p = placementOf(b.type);
    out.push({
      id: b.id, block: pin.block,
      message: say(S(p && p.message),
        pin.block + " must sit at position " + (pin.position === "first" ? "first" : pin.position === "last" ? "last" : String(pin.position)) + "."),
    });
  }
  /* 2 · must immediately follow */
  d.forEach((b, i) => {
    const f = followOf(b, tplName);
    if (!f || !f.targets.length) return;
    const present = f.targets.some((t) => d.some((x, j) => targetMatch(t, nameOf(x), d, j)));
    if (!present) return;                        /* no copy block in this template → inert */
    const prev = i > 0 ? d[i - 1] : null;
    const okHere = !!prev && f.targets.some((t) => targetMatch(t, nameOf(prev), d, i - 1));
    if (!okHere) {
      out.push({ id: b.id, block: nameOf(b), message: say(f.message, nameOf(b) + " must sit directly below " + f.targets.join(" or ") + ".") });
    }
  });
  /* 3 · must immediately precede */
  d.forEach((b, i) => {
    const p = precedeOf(b, tplName);
    if (!p || !p.targets.length) return;
    const present = p.targets.some((t) => d.some((x, j) => targetMatch(t, nameOf(x), d, j)));
    if (!present) return;
    const nx = i + 1 < len ? d[i + 1] : null;
    const okHere = !!nx && p.targets.some((t) => targetMatch(t, nameOf(nx), d, i + 1));
    if (!okHere) {
      out.push({ id: b.id, block: nameOf(b), message: say(p.message, nameOf(b) + " must sit directly above " + p.targets.join(" or ") + ".") });
    }
  });
  return out;
}

/**
 * The document after moving `fromId` to insertion index `to`, honouring units and pins.
 * Returns the ORIGINAL document plus a message when the move is refused, so a caller can
 * `setBlocks(reorder(...).doc)` unconditionally and show `message` when present.
 */
export function reorder(
  doc: PlaceBlock[] | null | undefined,
  fromId: string,
  to: number,
  tplName?: string,
  /** the ↑↓ arrows pass their direction; a drag passes nothing (its `to` is a drop line) */
  opts?: { step?: -1 | 1 },
): { doc: PlaceBlock[]; message?: string } {
  const d = (doc || []).slice();
  let ids = unitOf(d, fromId, tplName);
  if (!ids.length) return { doc: d };
  /* the FIRST card of a header run swapping with a later card of the same run is the
     card's own move, not the run's (2026-09-10: "card #2 between the header and card #1
     is a valid document") — only a move that leaves the run takes the header along */
  {
    const ci = d.findIndex((b) => b.id === fromId);
    const hr = headerRun(d, ci, tplName);
    if (hr && ci === hr.header + 1 && hr.end > ci && to >= ci && to <= hr.end) ids = [fromId];
  }
  const unit = ids.map((id) => d.find((b) => b.id === id)!).filter(Boolean);
  const rest = d.filter((b) => ids.indexOf(b.id) < 0);
  /* ── A GROUP IS ONE ITEM FOR ANYONE ELSE'S MOVE (Layne, ASK-JACK-MOVE-PAST-GROUP-
     20260903) ──────────────────────────────────────────────────────────────────────
     Moving the main image UP one place from under Body Copy + CTA used to be refused:
     the one-step destination landed INSIDE the pair, which is correctly invalid — but
     nobody asked for that state, it only exists because the arrow moves one position at
     a time. Moving the PAIR down past the image worked, so the same layout was reachable
     from one end and refused from the other. When the insertion index would split a
     `movesWith` unit, it now steps over the whole unit in the direction of travel, so
     "past the pair" is one logical step across two physical positions. A drop that
     lands inside a unit gets the same treatment — there is no valid document in there. */
  const fromIdx = d.findIndex((b) => b.id === ids[0]);
  /* ── THE STEP IS THE UNIT'S, NOT THE CLICKED BLOCK'S (2026-09-17) ─────────────────────
     The arrows pass "one place from where I sit". For a `movesWith` pair the clicked block
     leads its unit, so that index is also the unit's. A bracket is led from the MIDDLE:
     "subhead ↑" arrived as an insertion at the subhead's own index, which in the document
     with the trio removed is the spot the trio already occupied — a no-op — and "↓" jumped
     two blocks. Measured in the local build before this landed. So when the clicked block is
     not the unit's first, the one-step target is re-derived from the unit's edge. */
  const clickedIdx = d.findIndex((b) => b.id === fromId);
  /* ── A HEADER RUN TAKES ARROWS AND DRAGS ALIKE (2026-10-27) ──────────────────────────
     `to` arrives counted against the document with only the CLICKED block removed. The
     generic one-step re-derivation below would turn a long drag of the Listing Table into
     a single step, so a header run converts the index itself. An ARROW (`opts.step`) moves
     the whole run one block: up to just above the block over the header, down to just
     below the block under the run. A DRAG is re-counted against the document with the
     whole run removed, so a drop on the run's own edges is a no-op, not a step. */
  let runMapped = false;
  {
    const hr = headerRun(d, clickedIdx, tplName);
    if (hr && ids.length > 1 && ids[0] === d[hr.header].id) {
      const h = hr.header, e = hr.end;
      const step = opts && opts.step;
      if (step) to = step < 0 ? h - 1 : h + 1;
      else if (to > h) to = to >= e ? to - (e - h) : h;
      runMapped = true;
    }
  }
  if (!runMapped && ids.length > 1 && clickedIdx > fromIdx) to = to < clickedIdx ? fromIdx - 1 : fromIdx + 1;
  const dir: 1 | -1 = to > fromIdx ? 1 : -1;
  const at0 = Math.max(0, Math.min(rest.length, to));
  /* ── THE ASKED-FOR SPOT FIRST, THE STEP-OVER ONLY WHEN IT IS INVALID (2026-09-10) ──
     `unitsIn` binds Table Header to the FIRST Featured card, so card #2's one-step "up"
     stepped over the whole pair and landed ABOVE the header — correctly refused, and
     the only destination ever tried. But card #2 between the header and card #1 is a
     valid document (it simply becomes the first card), which is what the arrow meant.
     Jack: "not able to shift the first few up or down." So the literal insertion index
     is judged first; the step-over is the fallback for a spot that genuinely splits a
     unit, which is what Layne's ask was about. */
  const direct = canPlace(d, ids, at0, tplName);
  if (direct.ok) return { doc: [...rest.slice(0, at0), ...unit, ...rest.slice(at0)] };
  const at = stepOverUnits(rest, at0, dir, tplName);
  if (at === at0) return { doc: d, message: direct.message };
  const verdict = canPlace(d, ids, at, tplName);
  if (!verdict.ok) return { doc: d, message: verdict.message };
  return { doc: [...rest.slice(0, at), ...unit, ...rest.slice(at)] };
}

/**
 * Every BOUND PAIR in `doc` that no other block may land inside, as inclusive
 * [start, end] index pairs in order: a block sitting directly after the one it
 * `mustImmediatelyFollow` (the CTA row under Body Copy), and a block sitting directly
 * before the one it `mustImmediatelyPrecede` (Table Header over the listing table).
 * Adjacent pairs chain into one span (header → table → …). Single blocks are not listed.
 *
 * Wider than `unitOf` on purpose: `unitOf` is what MOVES TOGETHER (only `movesWith`,
 * Layne's explicit distinction); this is what CANNOT BE SPLIT by someone else's move.
 * Her ask named the `movesWith` group; her general form — "validate the intended
 * destination instead of each single-position step" — covers the header/table pair too,
 * and a one-step move that would split either is the same class of non-destination.
 */
export function unitsIn(doc: PlaceBlock[] | null | undefined, tplName?: string): [number, number][] {
  const d = doc || [];
  const bound: boolean[] = [];                         /* bound[i] = i and i+1 may not be split */
  for (let i = 0; i + 1 < d.length; i++) {
    const f = followOf(d[i + 1], tplName);
    const follows = !!f && f.targets.some((t) => targetMatch(t, nameOf(d[i]), d, i));
    const p = precedeOf(d[i], tplName);
    const precedes = !!p && p.targets.some((t) => targetMatch(t, nameOf(d[i + 1]), d, i + 1));
    /* a bracket (spacer · subhead · spacer) is one item for anyone else's move */
    const bu = bracketUnit(d, i);
    const bracketed = bu.length > 1 && bu.indexOf(d[i + 1].id) > -1;
    bound[i] = follows || precedes || bracketed;
  }
  const out: [number, number][] = [];
  let start = -1;
  for (let i = 0; i < d.length; i++) {
    if (bound[i]) { if (start < 0) start = i; }
    else if (start >= 0) { out.push([start, i]); start = -1; }
  }
  return out;
}

/**
 * If insertion index `at` would split a unit in `rest`, the index just past that unit
 * in the direction of travel; otherwise `at` unchanged. `rest` is the document with the
 * moving unit already removed — the same array `canPlace` builds.
 */
export function stepOverUnits(rest: PlaceBlock[], at: number, dir: 1 | -1, tplName?: string): number {
  for (const [s, e] of unitsIn(rest, tplName)) {
    if (at > s && at <= e) return dir > 0 ? e + 1 : s;
  }
  return at;
}

/** Would inserting a NEW block of `type` at `at` break a rule? Same verdict shape. */
export function insertBlocked(
  doc: PlaceBlock[] | null | undefined,
  type: string,
  at: number,
  tplName?: string,
): PlaceVerdict {
  const d = doc || [];
  const probe: PlaceBlock = { id: "__probe", type: S(type) };
  const split = splitsBracket(d, Math.max(0, Math.min(d.length, at)));
  if (split) return { ok: false, message: split };
  const next = [...d.slice(0, Math.max(0, Math.min(d.length, at))), probe, ...d.slice(Math.max(0, Math.min(d.length, at)))];
  const before = placementProblems(d, tplName).length;
  const problems = placementProblems(next, tplName);
  /* only NEW problems count: a document that already breaks a rule must not become
     un-editable because of it */
  if (problems.length > before) return { ok: false, message: problems[problems.length - 1].message };
  return { ok: true };
}
